/**
 * Amplifier profiles -- group 05 (low-feedback valve and valve-hybrid designs).
 *
 * These are the designs where the amplifier is not a voltage source. Output
 * impedances here run from a few tenths of an ohm to several ohms, global
 * feedback is usually zero, and every transformer-coupled entry has a real
 * bandwidth limit at both ends, so the loudspeaker impedance curve prints
 * itself onto the frequency response instead of being flattened out. Most of
 * these manufacturers publish no distortion figure; where that is true the
 * entry is tagged ESTIMATED and the provenance note says which numbers are
 * published and which are inferred.
 */

import type { AmpProfile } from '../../types/hardware';
import { HARMONIC_SHAPES, makeAmp, usd } from '../kit';

export const AMPS_05: AmpProfile[] = [
  makeAmp({
    mfr: 'Kondo',
    model: 'Kagura',
    price: usd(130000, 'APPROXIMATE', 2023, 'Approximate US retail per pair; Kondo pricing varies by market and this figure is not verified.'),
    year: 2013,
    country: 'Japan',
    topology: 'tube-set',
    form: 'monoblock',
    devices: '211 directly-heated triode, single-ended, silver-wound interstage and output transformers, no global feedback',
    power: { watts8: 55, watts4: 38, confidence: 'ESTIMATED' },
    gainDb: 27,
    thdPercent: 1.0,
    snrDb: 88,
    bandwidth: [12, 45000],
    outputImpedanceOhms: 2.4,
    feedbackDb: 0,
    outputTransformer: true,
    transformerLfHz: 15,
    transformerHfHz: 40000,
    crosstalkDb: -60,
    harmonicShape: HARMONIC_SHAPES.set,
    nonlinear: { kind: 'triode', amount: 0.88, asymmetry: 0.8, bias: 0.85, headroomDb: 8, oversample: 8 },
    dynamics: { sagDb: 1.8, sagTimeMs: 230, recoveryMs: 560, slewRateVus: 16, headroomDb: 1.0 },
    clipping: { kind: 'soft', kneeDb: 8.5, stickiness: 0.38 },
    confidence: 'ESTIMATED',
    accent: '#d8c08a',
    tags: ['set', '211', 'zero-feedback', 'silver-transformer', 'monoblock'],
    notes:
      'High-voltage single-ended 211 with silver-wound interstage and output transformers and no global loop. Distortion is dominated by a second harmonic that rises about a decibel per decibel of drive, so the residue tracks programme level rather than sitting at a fixed floor. With a 2.4 ohm source impedance the damping factor is around three, which means the modelled response is not flat into a real loudspeaker at all: it follows the impedance curve, lifting at the bass resonance peaks and at the rising treble impedance of a typical dome tweeter circuit.',
    provenanceNote:
      'Tube complement, single-ended zero-feedback topology and silver transformer construction are from Kondo published material. Power, THD, SNR, bandwidth and output impedance are ESTIMATED from single-ended 211 practice: Kondo does not publish distortion or noise specifications, and the widely quoted power rating is not confirmed against a datasheet here. Price is approximate.',
  }),

  makeAmp({
    mfr: 'Zanden Audio Systems',
    model: 'Model 8120',
    price: usd(29800, 'APPROXIMATE', 2020, 'Approximate US retail; Zanden distribution pricing varies and this figure is not verified.'),
    year: 2014,
    country: 'Japan',
    topology: 'tube-push-pull',
    form: 'stereo-power',
    devices: 'KT120 push-pull output pair per channel, transformer coupled, modest global feedback',
    power: { watts8: 100, watts4: 100, confidence: 'ESTIMATED' },
    gainDb: 29,
    thdPercent: 0.5,
    snrDb: 95,
    bandwidth: [10, 55000],
    outputImpedanceOhms: 0.9,
    feedbackDb: 10,
    outputTransformer: true,
    transformerLfHz: 13,
    transformerHfHz: 48000,
    crosstalkDb: -72,
    harmonicShape: HARMONIC_SHAPES.pushPullTube,
    nonlinear: { kind: 'pentode', amount: 0.55, asymmetry: 0.25, bias: 0.2, headroomDb: 11, oversample: 4 },
    dynamics: { sagDb: 0.9, sagTimeMs: 160, recoveryMs: 380, slewRateVus: 20, headroomDb: 1.4 },
    clipping: { kind: 'soft', kneeDb: 6, stickiness: 0.28 },
    confidence: 'ESTIMATED',
    accent: '#9fb8d8',
    tags: ['push-pull', 'kt120', 'transformer-coupled', 'moderate-feedback'],
    notes:
      'Push-pull operation cancels the even-order products that dominate the single-ended designs in this group, so the modelled residue is third-harmonic-led with a much smaller second harmonic. Ten decibels of global feedback pulls output impedance below one ohm, giving a damping factor near nine: enough to control a woofer resonance far better than a zero-feedback single-ended amplifier, but nowhere near the hundreds a solid-state design would provide, so some impedance-following remains. Supply sag is roughly half that of the single-ended entries because the push-pull output draws a much more constant current from the rail.',
    provenanceNote:
      'Push-pull valve topology and transformer coupling are from Zanden published material. Power, THD, SNR, output impedance, feedback and bandwidth are ESTIMATED from class norms; Zanden does not publish a full distortion and noise specification for this amplifier, and the output tube type is stated from product description rather than a datasheet.',
  }),

  makeAmp({
    mfr: 'Shindo Laboratory',
    model: 'Cortese',
    price: usd(16000, 'APPROXIMATE', 2019, 'Approximate US retail; Shindo was sold through a small dealer network and pricing is not verified.'),
    year: 2005,
    country: 'Japan',
    topology: 'tube-set',
    form: 'stereo-power',
    devices: 'F2a single-ended output, zero global feedback, Shindo-wound output transformers, paper-in-oil coupling',
    power: { watts8: 10, watts4: 7, confidence: 'ESTIMATED' },
    gainDb: 25,
    thdPercent: 1.5,
    snrDb: 82,
    bandwidth: [22, 25000],
    outputImpedanceOhms: 4.2,
    feedbackDb: 0,
    outputTransformer: true,
    transformerLfHz: 28,
    transformerHfHz: 22000,
    crosstalkDb: -54,
    harmonicShape: HARMONIC_SHAPES.set,
    nonlinear: { kind: 'triode', amount: 0.9, asymmetry: 0.75, bias: 0.8, headroomDb: 6, oversample: 8 },
    dynamics: { sagDb: 2.4, sagTimeMs: 260, recoveryMs: 640, slewRateVus: 11, headroomDb: 0.7 },
    clipping: { kind: 'soft', kneeDb: 10, stickiness: 0.45 },
    confidence: 'ESTIMATED',
    accent: '#c07f56',
    tags: ['set', 'f2a', 'zero-feedback', 'low-power', 'high-efficiency-partner'],
    notes:
      'Ten watts single-ended with a deliberately restricted transformer bandwidth, which is the point rather than a shortcoming: the low-frequency corner sits high enough that subsonic energy never reaches the core, and the top octave rolls gently. A 4.2 ohm source impedance gives a damping factor under two, so on any loudspeaker with a peaky impedance curve the response deviation from impedance interaction is larger than any voicing choice in the loudspeaker itself. Clipping arrives early and softly, with a ten-decibel knee and a long recovery, so the amplifier compresses rather than squares off.',
    provenanceNote:
      'Output tube, single-ended zero-feedback topology and in-house transformer and capacitor construction are from Shindo published material. Power, THD, SNR, bandwidth and output impedance are ESTIMATED: Shindo Laboratory published essentially no measured specifications. Year of introduction is approximate; the model was in production over a long period.',
  }),

  makeAmp({
    mfr: 'WAVAC Audio Lab',
    model: 'SH-833',
    price: usd(350000, 'APPROXIMATE', 2010, 'Approximate US retail per pair at the time of its US review; not verified against a current price list.'),
    year: 2005,
    country: 'Japan',
    topology: 'transformer-coupled',
    form: 'monoblock',
    devices: '833A transmitting triode, single-ended, WAVAC inverted interstage transformer drive circuit',
    power: { watts8: 150, watts4: 120, confidence: 'ESTIMATED' },
    gainDb: 26,
    thdPercent: 1.0,
    snrDb: 86,
    bandwidth: [15, 40000],
    outputImpedanceOhms: 1.6,
    feedbackDb: 0,
    outputTransformer: true,
    transformerLfHz: 18,
    transformerHfHz: 36000,
    crosstalkDb: -62,
    harmonicShape: HARMONIC_SHAPES.transformer,
    nonlinear: { kind: 'transformer-core', amount: 0.62, coreKnee: 0.48, hysteresis: 0.28, headroomDb: 9, oversample: 4 },
    dynamics: { sagDb: 1.2, sagTimeMs: 200, recoveryMs: 500, slewRateVus: 15, headroomDb: 1.1 },
    clipping: { kind: 'soft', kneeDb: 7.5, stickiness: 0.35 },
    confidence: 'ESTIMATED',
    accent: '#b07070',
    tags: ['833', 'interstage-transformer', 'zero-feedback', 'high-power-set'],
    notes:
      'The distinguishing feature is that the drive to the output triode is developed across a transformer rather than a resistive or active load, so there are two magnetic elements in the forward path and both contribute core behaviour. That is why this entry uses a transformer-core nonlinearity instead of the pure triode curve used for the single-ended entries here: the low-frequency distortion rises as flux density approaches the core knee, giving a distortion-versus-frequency slope that increases below about 60 Hz rather than staying flat. At 150 watts from a transmitting triode the clipping behaviour is still soft, but the amplifier reaches its transformer limits before it reaches its voltage limits.',
    provenanceNote:
      'Output tube, single-ended operation and the inverted interstage transformer drive circuit are from WAVAC published descriptions. Power, THD, SNR, bandwidth and output impedance are ESTIMATED; WAVAC does not publish distortion or noise data. Price is the widely reported US figure and is approximate rather than a verified current MSRP.',
  }),

  makeAmp({
    mfr: 'Allnic Audio',
    model: 'HPA-5000 XL',
    price: usd(7900, 'APPROXIMATE', 2021, 'Approximate US retail; Allnic dealer pricing varies and this figure is not verified.'),
    year: 2019,
    country: 'South Korea',
    topology: 'transformer-coupled',
    form: 'headphone',
    devices: 'Twin-triode voltage amplification into permalloy-cored output transformers with multiple impedance taps, no global feedback',
    power: { mw32: 1500, mw300: 1000, mw600: 700, confidence: 'ESTIMATED' },
    gainDb: 20,
    thdPercent: 0.3,
    snrDb: 90,
    bandwidth: [15, 40000],
    outputImpedanceOhms: 8,
    feedbackDb: 0,
    outputTransformer: true,
    transformerLfHz: 18,
    transformerHfHz: 36000,
    crosstalkDb: -66,
    harmonicShape: HARMONIC_SHAPES.transformer,
    nonlinear: { kind: 'transformer-core', amount: 0.5, coreKnee: 0.55, hysteresis: 0.2, headroomDb: 12, oversample: 4 },
    dynamics: { sagDb: 0.5, sagTimeMs: 150, recoveryMs: 320, slewRateVus: 18, headroomDb: 1.6 },
    clipping: { kind: 'soft', kneeDb: 6.5, stickiness: 0.3 },
    confidence: 'ESTIMATED',
    accent: '#8ec7a5',
    tags: ['headphone', 'output-transformer', 'zero-feedback', 'impedance-taps'],
    notes:
      'A valve headphone amplifier that drives the load through an output transformer rather than a capacitor or a direct-coupled cathode follower, which changes the loading behaviour completely. The tap arrangement keeps the reflected source impedance near an eighth of the selected load, so unlike a high-impedance valve output it does not turn a headphone impedance peak into a bass hump. The residual distortion is transformer-shaped: a dominant second harmonic with a slow-decaying tail that increases at low frequency as core flux rises, rather than the flat broadband floor of a feedback solid-state headphone amplifier.',
    provenanceNote:
      'Transformer-coupled output, permalloy core material and multiple output taps are from Allnic published descriptions. Output power into 32, 300 and 600 ohms, THD, SNR, bandwidth and reflected output impedance are all ESTIMATED from the design class; Allnic does not publish a full electrical specification for this amplifier, and the tube complement is described generically because the exact types are not confirmed here.',
  }),

  makeAmp({
    mfr: 'Thrax Audio',
    model: 'Enyo',
    price: usd(15000, 'APPROXIMATE', 2020, 'Approximate US retail; Thrax is priced in EUR and this conversion is not a verified US MSRP.'),
    year: 2016,
    country: 'Bulgaria',
    topology: 'hybrid',
    form: 'integrated',
    devices: 'Valve voltage amplification stage direct-coupled to a complementary MOSFET output stage',
    power: { watts8: 50, watts4: 100, confidence: 'ESTIMATED' },
    gainDb: 36,
    thdPercent: 0.05,
    snrDb: 100,
    bandwidth: [5, 80000],
    dampingFactor: 200,
    feedbackDb: 18,
    outputTransformer: false,
    crosstalkDb: -88,
    balancedInput: true,
    harmonicShape: HARMONIC_SHAPES.classA,
    nonlinear: { kind: 'triode', amount: 0.35, asymmetry: 0.45, bias: 0.5, headroomDb: 14, oversample: 4 },
    dynamics: { sagDb: 0.35, sagTimeMs: 70, recoveryMs: 180, thermalDb: 0.2, thermalTimeMs: 7000, slewRateVus: 45, headroomDb: 1.8 },
    clipping: { kind: 'asymmetric', kneeDb: 3.2, stickiness: 0.12 },
    confidence: 'ESTIMATED',
    accent: '#7ea8c4',
    tags: ['hybrid', 'mosfet-output', 'integrated', 'high-damping'],
    notes:
      'A valve gain stage feeding a transformerless MOSFET output, so the harmonic character comes from the small-signal valve while the load control comes from the semiconductor. The result sits deliberately between the two extremes in this group: a damping factor around 200 flattens loudspeaker impedance interaction almost completely, but the residual distortion is still low-order and second-harmonic-weighted rather than the higher-order residue of a high-feedback bipolar output stage. Bandwidth extends well past 50 kHz because there is no output transformer to impose a magnetic limit, and power doubles into four ohms, which none of the transformer-coupled entries here do.',
    provenanceNote:
      'Hybrid valve-input, solid-state-output topology and the integrated form are from Thrax published descriptions. Power, THD, SNR, damping factor, feedback and bandwidth are ESTIMATED from class norms; Thrax does not publish a complete distortion and noise specification for this model. Price is a currency conversion estimate.',
  }),

  makeAmp({
    mfr: 'Ypsilon Electronics',
    model: 'Aelius II',
    price: usd(56000, 'APPROXIMATE', 2019, 'Approximate US retail per pair; Ypsilon is priced in EUR and this figure is not verified.'),
    year: 2016,
    country: 'Greece',
    topology: 'hybrid',
    form: 'monoblock',
    devices: 'Valve input stage coupled through an in-house wound interstage transformer to a MOSFET output stage, no global feedback',
    power: { watts8: 200, watts4: 370, confidence: 'ESTIMATED' },
    gainDb: 30,
    thdPercent: 0.12,
    snrDb: 96,
    bandwidth: [8, 100000],
    outputImpedanceOhms: 0.35,
    feedbackDb: 0,
    outputTransformer: false,
    crosstalkDb: -86,
    balancedInput: true,
    harmonicShape: HARMONIC_SHAPES.transformer,
    nonlinear: { kind: 'transformer-core', amount: 0.4, coreKnee: 0.62, hysteresis: 0.14, headroomDb: 14, oversample: 4 },
    dynamics: { sagDb: 0.3, sagTimeMs: 80, recoveryMs: 200, thermalDb: 0.18, thermalTimeMs: 7000, slewRateVus: 55, headroomDb: 2.2 },
    clipping: { kind: 'asymmetric', kneeDb: 3.8, stickiness: 0.15 },
    confidence: 'ESTIMATED',
    accent: '#a89ad0',
    tags: ['hybrid', 'interstage-transformer', 'zero-global-feedback', 'monoblock'],
    notes:
      'Zero global feedback with a transformer between the valve gain stage and the MOSFET followers, which gives an unusual combination: the output impedance is low enough for a damping factor above twenty because the followers have local degeneration, but the distortion signature comes from the interstage core rather than a feedback loop. Distortion therefore rises at low frequency with flux density instead of falling with loop gain, and it stays second-harmonic-weighted at every level rather than turning into high-order products near clipping. Power doubling into four ohms and a bandwidth beyond 100 kHz distinguish it from every transformer-output design in this group.',
    provenanceNote:
      'Zero-global-feedback hybrid topology with an in-house wound interstage transformer is from Ypsilon published descriptions. Power, THD, SNR, output impedance and bandwidth are ESTIMATED; Ypsilon publishes very limited measured data. Price is an approximate currency conversion per pair.',
  }),

  makeAmp({
    mfr: 'Aries Cerat',
    model: 'Concero',
    price: usd(45000, 'APPROXIMATE', 2020, 'Approximate US retail; Aries Cerat is priced in EUR through a small dealer network and this figure is not verified.'),
    year: 2015,
    country: 'Cyprus',
    topology: 'tube-set',
    form: 'integrated',
    devices: '845 single-ended triode, choke-loaded driver stage, interstage transformer coupling, zero feedback, very large reservoir supply',
    power: { watts8: 50, watts4: 34, confidence: 'ESTIMATED' },
    gainDb: 33,
    thdPercent: 1.2,
    snrDb: 84,
    bandwidth: [12, 38000],
    outputImpedanceOhms: 1.9,
    feedbackDb: 0,
    outputTransformer: true,
    transformerLfHz: 16,
    transformerHfHz: 34000,
    crosstalkDb: -58,
    harmonicShape: HARMONIC_SHAPES.set,
    nonlinear: { kind: 'triode', amount: 0.8, asymmetry: 0.7, bias: 0.75, headroomDb: 10, oversample: 8 },
    dynamics: { sagDb: 0.6, sagTimeMs: 320, recoveryMs: 700, slewRateVus: 17, headroomDb: 1.9 },
    clipping: { kind: 'soft', kneeDb: 7, stickiness: 0.3 },
    confidence: 'ESTIMATED',
    accent: '#cf8f4a',
    tags: ['set', '845', 'choke-loaded', 'interstage-transformer', 'integrated'],
    notes:
      'An 845 single-ended integrated whose distinguishing electrical feature is the size of the energy storage behind it: modelled supply sag is 0.6 dB against 1.8 dB for the 211 monoblock in this group, so the dynamic behaviour under sustained load is much closer to a solid-state amplifier even though the steady-state distortion is a percent or more. Choke loading of the driver stage keeps the drive impedance high and constant, which preserves the strongly asymmetric second-harmonic transfer curve rather than linearising it. Output impedance just under two ohms still leaves a damping factor around four, so impedance interaction with the loudspeaker is significant.',
    provenanceNote:
      'Output tube, single-ended zero-feedback topology, choke loading and interstage transformer coupling are from Aries Cerat published descriptions. Power, THD, SNR, bandwidth and output impedance are ESTIMATED from single-ended 845 practice; Aries Cerat does not publish distortion or noise figures. Price is approximate.',
  }),
];
